import { useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import {
  Box,
  Container,
  Grid,
  TableRow,
  TableCell,
  Tooltip,
  Typography,
  Button,
  ButtonBase,
  IconButton,
  CircularProgress,
  Avatar,
  Menu,
  useTheme
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

import {
  faIndustry,
  faDollarSign,
  faCheck,
  faTrash,
  faClipboardQuestion,
  faEnvelope,
  faUserCheck
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import {
  Checkbox,
  CategoriesAvatar,
  LoadingButton,
  DefaultPaper,
  Select
} from "@aclymatepackages/atoms";
import {
  formatDate,
  formatDecimal,
  letterSBoolean,
  ucFirstLetters
} from "@aclymatepackages/formatters";
import findSubcategory from "@aclymatepackages/subcategories";
import { truncateText } from "@aclymatepackages/other-helpers";
import mainTheme from "@aclymatepackages/themes";

import VendorDetailsSlider from "./VendorDetailsSlider";
import VendorCleanupView from "./cleanup/VendorCleanupView";
import CleanupCalloutBanner from "./cleanup/CleanupCalloutBanner";
import EditVendorDetails from "./EditVendorDetails";
import VendorsGraphs from "./VendorsGraph";
import SettingsObjectDetailsSlider from "../../SettingsObjectDetailsSlider";
import EditRowTransactions from "../../EditRowTransactions";

import VendorCategoryChip from "../../../../atoms/icons/VendorCategoryChip";
import NullableInput from "../../../../atoms/inputs/NullableInput";
import DownloadButton from "../../../../atoms/buttons/DownloadButton";
import ScopeThreeCategoryAvatar from "../../../../atoms/icons/ScopeThreeCategoryAvatar";
import CopyToClipboardIcon from "../../../../atoms/buttons/CopyToClipboardIcon";
import ButtonMenu from "../../../../atoms/buttons/ButtonMenu";
import FeatureErrorBoundary from "../../../../atoms/FeatureErrorBoundary";

import ActionItemsModule from "../../../../modules/ActionItemsModule";
import SortableTable, {
  makeColumnObj
} from "../../../../modules/tables/SortableTable";
import DashboardViewLayout from "../../../../layouts/DashboardViewLayout";
import { MissingVendors } from "../../../../layouts/missingSettingsLayouts";
import VendorInputForm from "../../../../inputs/vendors/VendorInputForm";
import EmissionsCategorySelect from "../../../../inputs/vendors/EmissionsCategorySelect";
import ScopeThreeCategorySelect from "../../../../inputs/vendors/ScopeThreeCategorySelect";
import OpenAiVendorCategorization from "../../../../inputs/vendors/OpenAiVendorCategorization";
import useStatusFilterChips from "../../../../hooks/statusFilterChips";

import usePlatformLayoutContext from "../../../../../helpers/contexts/platformLayout";
import usePermissions from "../../../../../helpers/hooks/usePermissions";
import {
  vendorStatuses,
  surveyDataStatusMap,
  useOnVendorSave,
  useUpdateVendorRelationshipFields,
  archiveVendorTransactions
} from "../../../../../helpers/components/vendors";
import {
  useCachedDisplayData,
  useAccountData
} from "../../../../../helpers/firebase";
import useEmissionsContext from "../../../../../helpers/contexts/emissions";
import useProductsEmissionsFactors, {
  ProductsEmissionsFactorsContextProvider
} from "../../../../../helpers/contexts/ProductsEmissionsFactors";
import { calculateMergedProductFootprint } from "@aclymatepackages/products";
import PlatformProductDetailsSlider from "../../../products/PlatformProductDetailsSlider";
import ProductReport from "../../../products/ProductReport";
import { getAccountId } from "../../../../../helpers/accountHelpers";
import { convertDataToCsv } from "../../../../../helpers/otherHelpers";
import { isProductLinkedToVendor } from "../../../../../helpers/components/products";
import useDisplayMeasurementSystem from "../../../../../helpers/hooks/displayMeasurementSystem";

const camelCaseToCapitalCase = (string) =>
  ucFirstLetters(string.replace(/([A-Z])/g, " $1"));

const buildVendorSurveyStatusMap = () => {
  const surveyDataEntries = Object.fromEntries(
    Object.entries(surveyDataStatusMap).map(([key, value]) => [
      `survey-${key}`,
      value
    ])
  );

  return {
    "not-invited": {
      label: "Not Invited",
      icon: faClipboardQuestion,
      color: mainTheme.palette.text.secondary,
      tooltip: "This vendor hasn't been invited to share their data yet."
    },
    invited: {
      label: "Invited",
      icon: faEnvelope,
      color: mainTheme.palette.warning.dark,
      tooltip: "An invitation has been sent. Awaiting account creation."
    },
    "has-explorer-account": {
      label: "Has Explorer Account",
      icon: faUserCheck,
      color: mainTheme.palette.secondary.main,
      tooltip: "This vendor has created their Explorer account."
    },
    ...surveyDataEntries
  };
};

const buildVendorSurveyStatusOptions = (vendorSurveyStatusMap) =>
  Object.entries(vendorSurveyStatusMap).map(
    ([value, { label, icon, color }]) => ({
      value,
      label,
      avatar: (
        <Avatar style={{ height: 30, width: 30, backgroundColor: color }}>
          <FontAwesomeIcon icon={icon} style={{ color: "white" }} />
        </Avatar>
      ),
      style: {
        backgroundColor: color,
        color: "white"
      }
    })
  );

const findVendorSurveyStatusDetails = ({
  vendorSurveyStatusMap,
  vendorSurveyStatus
}) =>
  vendorSurveyStatusMap[vendorSurveyStatus] ||
  vendorSurveyStatusMap["not-invited"];

const useOnArchiveVendor = () => {
  const { triggerTransactionUpdateCountdown } = usePlatformLayoutContext();
  const updateVendorRelationshipFields = useUpdateVendorRelationshipFields();

  return async ({ id, vendorId }) => {
    await updateVendorRelationshipFields(id, { archived: true });
    await archiveVendorTransactions(vendorId);
    return triggerTransactionUpdateCountdown();
  };
};

const InlineEditingPopper = ({
  ButtonComponent,
  displayComponent,
  Editor,
  vendor,
  disabled
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <>
      <ButtonComponent
        onClick={(e) => {
          e.stopPropagation();
          return setAnchorEl(e.currentTarget);
        }}
        disabled={disabled}
      >
        {displayComponent}
      </ButtonComponent>
      <Menu
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={(e) => {
          e.stopPropagation();
          setAnchorEl(null);
        }}
      >
        <Box
          p={1}
          style={{ width: "550px", maxHeight: "350px", overflowY: "auto" }}
        >
          <Editor setAnchorEl={setAnchorEl} vendor={vendor} />
        </Box>
      </Menu>
    </>
  );
};

const InlineEmissionCategorySelectEditor = ({ setAnchorEl, vendor }) => {
  const { onVendorSave } = useOnVendorSave(() => setAnchorEl(null));
  const {
    naicsCode: disposableNaicsCode,
    naicsTitle: disposableNaicsTitle,
    ...otherVendorProps
  } = vendor;

  const [editableVendor, setEditableVendor] = useState(otherVendorProps);
  const { naicsCode, naicsTitle, emissionCategory, description } =
    editableVendor;

  const editVendor = (field) => (value) =>
    editObjectData(setEditableVendor, field, value);

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Typography variant="body2" align="center">
          {description}
        </Typography>
      </Grid>
      <Grid item>
        <EmissionsCategorySelect
          value={emissionCategory}
          setValue={editVendor("emissionCategory")}
        />
      </Grid>
      {emissionCategory === "spend-based" && (
        <Grid item>
          <OpenAiVendorCategorization
            editVendor={editVendor}
            naicsCode={naicsCode}
            naicsTitle={naicsTitle}
          />
        </Grid>
      )}
      <Grid item container justifyContent="center">
        <Grid item>
          <Button
            size="small"
            variant="contained"
            disabled={
              emissionCategory === "spend-based" && (!naicsCode || !naicsTitle)
            }
            onClick={(e) => {
              e.stopPropagation();
              return onVendorSave(editableVendor);
            }}
          >
            Save Edits
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const VendorCategoryChipEditor = ({ vendor, canEdit }) => {
  return (
    <InlineEditingPopper
      ButtonComponent={ButtonBase}
      displayComponent={<VendorCategoryChip {...vendor} />}
      Editor={InlineEmissionCategorySelectEditor}
      vendor={vendor}
      disabled={!canEdit}
    />
  );
};

const ScopeThreeCategorySelectEditor = ({ setAnchorEl, vendor }) => {
  const { onVendorSave } = useOnVendorSave(() => setAnchorEl(null));

  const { scopeThreeCategory } = vendor;

  return (
    <ScopeThreeCategorySelect
      scopeThreeCategory={scopeThreeCategory}
      setScopeThreeCategory={(scopeThreeCategory) =>
        onVendorSave({
          ...vendor,
          scopeThreeCategory
        })
      }
    />
  );
};

const ScopeThreeSubcategorySelectButton = ({ vendor, canEdit }) => {
  const { scopeThreeCategory, emissionCategory } = vendor;

  return (
    <InlineEditingPopper
      ButtonComponent={IconButton}
      displayComponent={
        <ScopeThreeCategoryAvatar scopeThreeCategory={scopeThreeCategory} />
      }
      Editor={ScopeThreeCategorySelectEditor}
      vendor={vendor}
      disabled={!canEdit || emissionCategory !== "spend-based"}
    />
  );
};

const VendorsTableRow = ({
  vendor,
  setSelectedVendor,
  checkedVendors,
  setCheckedVendors
}) => {
  const {
    id,
    status,
    name: vendorName,
    tonsCo2e,
    tonsCo2ePerDollar,
    totalTonsCo2ePercentage,
    avgTonsCo2ePerTransaction,
    transactionsCount,
    transactionsCountPercentage,
    scope,
    emissionCategory,
    vendorSurveyStatus,
    linkedProductsCount
  } = vendor;

  const {
    icon: statusIcon,
    color: statusColor,
    tooltip: statusTooltip
  } = vendorStatuses[status] || {};

  const { onVendorSave } = useOnVendorSave();
  const { palette } = useTheme();
  const archiveVendor = useOnArchiveVendor();
  const { canEdit } = usePermissions();

  const vendorSurveyStatusMap = buildVendorSurveyStatusMap();

  const {
    icon: vendorSurveyStatusIcon,
    color: vendorSurveyStatusColor,
    tooltip: vendorSurveyStatusTooltip
  } = findVendorSurveyStatusDetails({
    vendorSurveyStatusMap,
    vendorSurveyStatus
  });

  const formatPercentage = (percentage = 0) => {
    const displayDigits = percentage > 0.01 ? 2 : 6;
    return `${formatDecimal(percentage, displayDigits)}%`;
  };

  const { convertCarbonUnits } = useDisplayMeasurementSystem();

  const onCheckVendor = (isChecked) => {
    if (isChecked) {
      return setCheckedVendors((currentlyCheckedVendors) => [
        ...currentlyCheckedVendors,
        vendor
      ]);
    }

    return setCheckedVendors((currentlyCheckedVendors) =>
      currentlyCheckedVendors.filter((vendor) => vendor.id !== id)
    );
  };

  const actions = [
    {
      icon: faTrash,
      tooltip: "Delete Vendor",
      color: "error",
      disabled: !canEdit,
      onClick: (e) => {
        e.stopPropagation();
        return archiveVendor(vendor);
      }
    },
    {
      icon: faCheck,
      tooltip: "Confirm Vendor",
      color: "secondary",
      disabled: status !== "unconfirmed",
      onClick: (e) => {
        e.stopPropagation();
        return onVendorSave(vendor);
      }
    }
  ];

  const tableCells = [
    <Checkbox
      key="select"
      value={!!checkedVendors.find((vendor) => vendor.id === id)}
      editValue={onCheckVendor}
    />,
    <Tooltip key="status" title={statusTooltip}>
      <span>
        <FontAwesomeIcon
          icon={statusIcon}
          size="2x"
          style={{ color: statusColor }}
        />
      </span>
    </Tooltip>,
    truncateText(vendorName, 25),
    <VendorCategoryChipEditor key="category" vendor={vendor} canEdit={canEdit} />,
    emissionCategory !== "spend-based" && scope !== 3 ? (
      <Box key="subcategory" ml={1.5}>&quot;N/A&quot;</Box>
    ) : (
      <ScopeThreeSubcategorySelectButton
        key="subcategory"
        vendor={vendor}
        canEdit={canEdit}
      />
    ),
    <Tooltip key="survey-status" title={vendorSurveyStatusTooltip}>
      <span>
        <FontAwesomeIcon
          icon={vendorSurveyStatusIcon}
          size="2x"
          style={{ color: vendorSurveyStatusColor }}
        />
      </span>
    </Tooltip>,
    `${formatDecimal(convertCarbonUnits(tonsCo2e))} (${formatPercentage(
      totalTonsCo2ePercentage
    )})`,
    avgTonsCo2ePerTransaction ? formatDecimal(tonsCo2ePerDollar) : "N/A",
    `${formatDecimal(transactionsCount)} (${formatPercentage(
      transactionsCountPercentage
    )})`,
    linkedProductsCount || "—",
    <Grid key="actions" container wrap="nowrap">
      {actions.map(({ icon, tooltip, onClick, color, disabled }) => (
        <Grid key={`vendor-action-${tooltip}`} item>
          <Tooltip title={tooltip}>
            <span>
              <IconButton onClick={onClick} disabled={disabled}>
                <FontAwesomeIcon
                  icon={icon}
                  size="sm"
                  style={{ color: !disabled ? palette[color].main : "inherit" }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      ))}
    </Grid>
  ];
  return (
    <TableRow hover onClick={() => setSelectedVendor(vendor)}>
      {tableCells.map((cell, idx) => (
        <TableCell key={`vendor-table-row-cell-${idx}`}>{cell}</TableCell>
      ))}
    </TableRow>
  );
};

const CheckedVendorsActionBlock = ({
  checkedVendors,
  setCheckedVendors,
  setBulkEditVendors
}) => {
  const { onVendorSave } = useOnVendorSave();
  const { palette } = useTheme();
  const onArchiveVendor = useOnArchiveVendor();

  const [deleteLoading, setDeleteLoading] = useState(false);

  const onBulkVendorsDelete = async () => {
    setDeleteLoading(true);
    await Promise.all(checkedVendors.map(onArchiveVendor));
    return setCheckedVendors([]);
  };

  const areAnyVendorsConfirmed = checkedVendors.find(
    ({ status }) => status === "confirmed"
  );

  const vendorsCountString = `${checkedVendors.length} vendor${letterSBoolean(
    checkedVendors
  )}`;

  const onBulkVendorsConfirm = () => {
    checkedVendors.forEach((vendor) => onVendorSave(vendor));
    return setCheckedVendors([]);
  };

  return (
    <DefaultPaper>
      <Grid
        container
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
      >
        <Grid item>
          <Typography variant="h6">Edit {vendorsCountString}</Typography>
        </Grid>
        <Grid item>
          <Grid
            container
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
          >
            {checkedVendors.length > 1 && (
              <Grid item>
                <Tooltip title={`Edit ${checkedVendors.length} vendors`}>
                  <IconButton onClick={() => setBulkEditVendors(true)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            <Grid item>
              <Tooltip title="Confirm Vendors">
                <IconButton onClick={onBulkVendorsConfirm}>
                  <FontAwesomeIcon
                    style={{ color: palette.secondary.main }}
                    icon={faCheck}
                  />
                </IconButton>
              </Tooltip>
            </Grid>
            {!areAnyVendorsConfirmed && (
              <Grid item>
                {deleteLoading ? (
                  <CircularProgress />
                ) : (
                  <Tooltip title={`Delete ${vendorsCountString}`}>
                    <IconButton onClick={onBulkVendorsDelete}>
                      <DeleteIcon style={{ color: palette.error.main }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
            )}
            <Grid item>
              <Tooltip title="Clear Selection">
                <IconButton onClick={() => setCheckedVendors([])}>
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </DefaultPaper>
  );
};

const BulkVendorsEditor = ({
  checkedVendors,
  warning,
  onSave,
  cta,
  alternateName,
  initialVendor = {},
  setSelectedObject
}) => {
  const [bulkVendorData, setBulkVendorData] = useState(initialVendor);
  const [saveLoading, setSaveLoading] = useState(false);

  const { emissionCategory, naicsCode, scopeThreeCategory, name } =
    bulkVendorData;

  const editBulkVendorData = (field) => (value) =>
    editObjectData(setBulkVendorData, field, value);

  const isSaveEnabled = () => {
    if (!(name || alternateName) || !emissionCategory) {
      return false;
    }

    if (emissionCategory !== "spend-based") {
      return true;
    }

    const doAllVendorsHaveNaicsCodes = checkedVendors.every(
      ({ naicsCode }) => !!naicsCode
    );

    if ((doAllVendorsHaveNaicsCodes || naicsCode) && scopeThreeCategory) {
      return true;
    }

    return false;
  };

  return (
    <SettingsObjectDetailsSlider
      type="vendors"
      settingsObject={bulkVendorData}
      objectName={alternateName || name}
      editNameDisabled={!!alternateName}
      editSettingsObject={editBulkVendorData}
      setSelectedObject={setSelectedObject}
      leftContent={
        <Container maxWidth="sm">
          <Grid container direction="column" spacing={2}>
            {checkedVendors.map(({ name, emissionCategory }, idx) => (
              <Grid
                item
                key={`checked-vendor-${idx}`}
                container
                spacing={2}
                alignItems="center"
              >
                <Grid item>
                  <CategoriesAvatar subcategory={emissionCategory} />
                </Grid>
                <Grid item>
                  <Typography variant="h3" style={{ color: "white" }}>
                    {name}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Container>
      }
      views={[
        {
          label: "Details",
          value: "details",
          content: (
            <EditVendorDetails
              editVendor={editBulkVendorData}
              vendor={bulkVendorData}
            />
          ),
          footer: (
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              spacing={2}
              wrap="nowrap"
            >
              {warning && (
                <Grid item>
                  <Typography variant="body2" color="error" align="right">
                    {warning}
                  </Typography>
                </Grid>
              )}
              <Grid item>
                <LoadingButton
                  isLoading={saveLoading}
                  onClick={() => onSave(bulkVendorData, setSaveLoading)}
                  variant="contained"
                  color="primary"
                  label={`${cta} ${
                    checkedVendors.length
                  } vendor${letterSBoolean(checkedVendors)}`}
                  disabled={!isSaveEnabled()}
                />
              </Grid>
            </Grid>
          )
        }
      ]}
    />
  );
};

const EditBulkVendors = ({
  checkedVendors,
  setCheckedVendors,
  setBulkEditVendors
}) => {
  const { onVendorSave } = useOnVendorSave();

  const onBulkVendorSave = async (bulkVendorData, setSaveLoading) => {
    setSaveLoading(true);
    await Promise.all(
      checkedVendors.map(
        async (vendor) =>
          await onVendorSave({
            ...vendor,
            ...bulkVendorData
          })
      )
    );

    setBulkEditVendors(false);
    return setCheckedVendors([]);
  };

  const buildInitialVendor = () => {
    if (!checkedVendors.length) {
      return {};
    }

    const uniqueCheckedVendorsSubcategories = [
      ...new Set(checkedVendors.map(({ emissionCategory }) => emissionCategory))
    ];

    if (uniqueCheckedVendorsSubcategories.length > 1) {
      return {};
    }

    const {
      naicsCode: initialNaicsCode,
      scopeThreeCategory: initialScopeThreeCategory,
      emissionCategory: initialEmissionCategory
    } = checkedVendors[0];

    const uniqueCheckedVendorsNaicsCodes = [
      ...new Set(checkedVendors.map(({ naicsCode }) => naicsCode))
    ];
    const uniqueCheckedVendorsScopeThreeCategories = [
      ...new Set(
        checkedVendors.map(({ scopeThreeCategory }) => scopeThreeCategory)
      )
    ];

    const initialNaicsCodeObj =
      uniqueCheckedVendorsNaicsCodes.length === 1
        ? { naicsCode: initialNaicsCode }
        : {};

    const initialScopeThreeCategoryObj =
      uniqueCheckedVendorsScopeThreeCategories.length === 1
        ? { scopeThreeCategory: initialScopeThreeCategory }
        : {};

    return {
      emissionCategory: initialEmissionCategory,
      ...initialNaicsCodeObj,
      ...initialScopeThreeCategoryObj
    };
  };

  return (
    <BulkVendorsEditor
      alternateName={`Bulk Edit ${checkedVendors.length} Vendors`}
      checkedVendors={checkedVendors}
      onSave={onBulkVendorSave}
      initialVendor={buildInitialVendor()}
      cta="Edit"
      setSelectedObject={setBulkEditVendors}
    />
  );
};

const NewVendorsButtonMenu = ({ setShowNewVendorInput }) => {
  const [popperOpen, setPopperOpen] = useState(false);
  const { canEdit, canShare } = usePermissions();

  const accountId = getAccountId();

  const surveyLink = `https://${window.location.host}/explorer/create-account?buyerId=${accountId}`;

  const menuOptions = [
    ...(canEdit
      ? [
          {
            label: "Add new vendors",
            onClick: () => setShowNewVendorInput(true)
          }
        ]
      : []),
    ...(canShare
      ? [{ label: "Send a Survey", onClick: () => setPopperOpen(true) }]
      : [])
  ];

  return (
    <Box position="relative">
      <ButtonMenu color="primary" menuOptions={menuOptions} />
      {popperOpen && (
        <DefaultPaper
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            zIndex: 1000
          }}
        >
          <Grid container direction="column" spacing={2} wrap="nowrap">
            <Grid
              item
              container
              justifyContent="space-between"
              spacing={2}
              wrap="nowrap"
              alignItems="center"
            >
              <Grid item>
                <Typography variant="h4">
                  Have your vendors take a survey
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  When your vendors take a survey using the link below, their
                  emissions will automatically be added to your accounting.
                </Typography>
              </Grid>
              <Grid item>
                <IconButton onClick={() => setPopperOpen(false)}>
                  <CancelIcon />
                </IconButton>
              </Grid>
            </Grid>
            <Grid item container spacing={2} wrap="nowrap" alignItems="center">
              <Grid item>
                <CopyToClipboardIcon text={surveyLink} />
              </Grid>
              <Grid item>
                <Typography variant="caption" color="textSecondary" noWrap>
                  {surveyLink}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </DefaultPaper>
      )}
    </Box>
  );
};

const singleVendorsLoop = ({
  vendors,
  filterByChips,
  filterSubcategories,
  filterScopeThreeCategory,
  filterVendorSurveyStatus,
  searchString,
  dbProducts,
  productLifecycleEvents
}) => {
  let vendorsDisplayObj = {
    vendorsTonsCo2e: 0,
    chartVendors: [],
    filteredVendors: [],
    availableSubcategories: []
  };

  if (!vendors.length) {
    return vendorsDisplayObj;
  }

  vendors.forEach((vendor) => {
    const {
      tonsCo2e,
      emissionCategory,
      name,
      scopeThreeCategory,
      vendorSurveyStatus
    } = vendor || {};

    if (tonsCo2e) {
      vendorsDisplayObj.vendorsTonsCo2e += tonsCo2e;
      vendorsDisplayObj.chartVendors.push(vendor);
    }

    if (!vendorsDisplayObj.availableSubcategories.includes(emissionCategory)) {
      vendorsDisplayObj.availableSubcategories.push(emissionCategory);
    }

    const filterBySubcategory = () => {
      if (!filterSubcategories.length) {
        return true;
      }

      const filterSubcategoriesArray = filterSubcategories.map(
        ({ value }) => value
      );

      if (!emissionCategory) {
        return filterSubcategoriesArray.includes("uncategorized");
      }

      return filterSubcategoriesArray.includes(emissionCategory);
    };

    const filterByScopeThreeCategory = () => {
      if (!filterScopeThreeCategory) {
        return true;
      }

      if (filterScopeThreeCategory === -1) {
        return !scopeThreeCategory;
      }

      return scopeThreeCategory === filterScopeThreeCategory;
    };

    const filterBySearchString = () => {
      if (!searchString) {
        return true;
      }

      return name.toLowerCase().includes(searchString.toLowerCase());
    };

    const filterByVendorSurveyStatus = () => {
      if (!filterVendorSurveyStatus) {
        return true;
      }

      return vendorSurveyStatus === filterVendorSurveyStatus;
    };

    if (
      filterByChips(vendor) &&
      filterBySubcategory() &&
      filterBySearchString() &&
      filterByScopeThreeCategory() &&
      filterByVendorSurveyStatus()
    ) {
      const linkedProductsCount = dbProducts.filter((product) =>
        isProductLinkedToVendor(product, vendor, productLifecycleEvents)
      ).length;
      vendorsDisplayObj.filteredVendors.push({
        ...vendor,
        linkedProductsCount
      });
    }
  });

  return vendorsDisplayObj;
};

const VendorCsvDownloadButton = ({ vendors }) => {
  const prepareCsv = () => {
    const formattedVendors = vendors.map(
      ({
        status,
        name,
        emissionCategory,
        naicsCode,
        naicsTitle,
        description,
        scopeThreeCategory,
        vendorSurveyStatus,
        tonsCo2e,
        totalTonsCo2ePercentage,
        avgTonsCo2ePerTransaction,
        transactionsCount,
        transactionsCountPercentage,
        vendorScore
      }) => ({
        status,
        name,
        emissionCategory,
        naicsCode,
        naicsTitle,
        description,
        scopeThreeCategory,
        vendorSurveyStatus,
        tonsCo2e: formatDecimal(tonsCo2e),
        totalTonsCo2ePercentage: formatDecimal(totalTonsCo2ePercentage),
        avgTonsCo2ePerTransaction: formatDecimal(avgTonsCo2ePerTransaction),
        transactionsCount: formatDecimal(transactionsCount),
        transactionsCountPercentage: formatDecimal(transactionsCountPercentage),
        vendorScore
      })
    );

    const csvHeaders = Object.keys(formattedVendors[0] || {}).map((header) =>
      camelCaseToCapitalCase(header)
    );

    return convertDataToCsv(formattedVendors, csvHeaders);
  };

  return (
    <DownloadButton
      buildFile={prepareCsv}
      tooltip="Download Filtered Vendors"
      fileName={`${formatDate(new Date())}-aclymate-vendors-download.csv`}
    />
  );
};

const VENDORS_TABLE_ROWS_PER_PAGE = 50;

const VendorsViewBlock = ({ viewLoading }) => {
  const { action } = useParams();
  const { rootPath } = usePlatformLayoutContext();

  if (action === "cleanup") {
    return <VendorCleanupView rootPath={rootPath} />;
  }

  return <VendorsViewTableBlock viewLoading={viewLoading} rootPath={rootPath} />;
};

const VendorsViewTableBlock = ({ viewLoading, rootPath }) => {
  const { emissionsLoading, vendors } = useEmissionsContext();
  const { carbonUnitLabel, convertCarbonUnits } = useDisplayMeasurementSystem();
  const [{ name: companyName }] = useAccountData();
  const [{ emissionsFactors }] = useProductsEmissionsFactors();
  const [dbProducts] = useCachedDisplayData("product-components");
  const [productLifecycleEvents] = useCachedDisplayData(
    "product-lifecycle-events"
  );

  const vendorSurveyStatusMap = buildVendorSurveyStatusMap();
  const vendorSurveyStatusOptions = buildVendorSurveyStatusOptions(
    vendorSurveyStatusMap
  );

  const { filterChips, filterByChips } = useStatusFilterChips(
    vendors,
    vendorStatuses
  );

  const history = useHistory();
  const location = useLocation();
  const selectedVendorId = new URLSearchParams(location.search).get("id");
  const selectedVendor = vendors.find((v) => v.id === selectedVendorId) ?? null;
  const setSelectedVendor = (vendor) =>
    vendor
      ? history.replace({ search: `?id=${vendor.id}` })
      : history.replace({ search: "" });

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [checkedVendors, setCheckedVendors] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [reportProduct, setReportProduct] = useState(null);
  const [vendorSearchString, setVendorSearchString] = useState("");
  const [filterSubcategories, setFilterSubcategories] = useState([]);
  const [filterScopeThreeCategory, setFilterScopeThreeCategory] = useState("");
  const [filterVendorSurveyStatus, setFilterVendorSurveyStatus] = useState("");
  const [showNewVendorInput, setShowNewVendorInput] = useState(false);
  const [bulkEditVendors, setBulkEditVendors] = useState(false);
  const [tablePage, setTablePage] = useState(0);

  const {
    vendorsTonsCo2e,
    chartVendors,
    filteredVendors,
    availableSubcategories
  } = singleVendorsLoop({
    vendors,
    filterByChips,
    filterSubcategories,
    filterScopeThreeCategory,
    filterVendorSurveyStatus,
    searchString: vendorSearchString,
    dbProducts,
    productLifecycleEvents
  });

  const selectedProductDoc = dbProducts.find(
    ({ id }) => id === selectedProductId
  );
  const selectedProductLifecycle =
    productLifecycleEvents.find(
      ({ productId }) => productId === selectedProductId
    ) || {};
  const selectedProduct = selectedProductDoc
    ? calculateMergedProductFootprint(
        selectedProductDoc,
        selectedProductLifecycle,
        emissionsFactors
      )
    : null;

  const selectVendorFromGraph = (id) =>
    history.replace({ search: `?id=${id}` });

  const buildFilterRows = () => {
    const availableSubcategoriesArray = availableSubcategories
      .filter((subcategory) => subcategory)
      .map((subcategory) => findSubcategory(subcategory));

    return [
      {
        icon: faIndustry,
        title: "Emission Category",
        type: "subcategory",
        filterComponent: (
          <EmissionsCategorySelect
            multiple
            value={filterSubcategories}
            setValue={setFilterSubcategories}
            availableSubcategories={availableSubcategoriesArray}
            additionalSubcategories={[
              {
                label: "Uncategorized",
                value: "uncategorized",
                avatar: (
                  <Avatar style={{ height: 30, width: 30 }}>
                    <QuestionMarkIcon />
                  </Avatar>
                )
              }
            ]}
          />
        )
      },
      {
        icon: faDollarSign,
        title: "Scope 3 Category",
        type: "scopeThreeCategory",
        filterComponent: (
          <ScopeThreeCategorySelect
            scopeThreeCategory={filterScopeThreeCategory}
            setScopeThreeCategory={setFilterScopeThreeCategory}
          />
        )
      },
      {
        icon: faClipboardQuestion,
        title: "Data Sharing Status",
        type: "vendorSurveyStatus",
        filterComponent: (
          <Select
            value={filterVendorSurveyStatus}
            editValue={setFilterVendorSurveyStatus}
            valueAvatar={
              filterVendorSurveyStatus
                ? vendorSurveyStatusOptions.find(
                    ({ value }) => value === filterVendorSurveyStatus
                  )?.avatar
                : null
            }
            options={[...vendorSurveyStatusOptions]}
            size="small"
            style={{ minWidth: "200px" }}
            label="Select a Vendor Survey Status"
          />
        )
      }
    ];
  };

  const onCheckAllVendors = (sortedVendors) => (isChecked) => {
    if (isChecked) {
      return setCheckedVendors(sortedVendors);
    }
    return setCheckedVendors([]);
  };

  const tableColumns = [
    {
      check: true
    },
    makeColumnObj("STATUS", "severity", true),
    makeColumnObj("NAME", "name", true),
    makeColumnObj("EMISSION CATEGORY", "emissionCategory", true),
    makeColumnObj("SCOPE 3 CATEGORY", "scopeThreeCategory", true),
    makeColumnObj("DATA SHARING STATUS", "vendorSurveyStatus", true),
    makeColumnObj(
      `TOTAL ${carbonUnitLabel.toUpperCase()} CO2`,
      "tonsCo2e",
      true
    ),
    makeColumnObj(`TONS CO2e PER DOLLAR`, "tonsCo2ePerDollar", true),
    makeColumnObj("TOTAL # OF TRANSACTIONS", "transactionsCount", true),
    makeColumnObj("LINKED PRODUCTS", "linkedProductsCount", true),
    makeColumnObj("ACTIONS", "actions", false)
  ];

  return (
    <>
      {showNewVendorInput && (
        <VendorInputForm setOpen={setShowNewVendorInput} />
      )}
      {bulkEditVendors && (
        <EditBulkVendors
          checkedVendors={checkedVendors}
          setCheckedVendors={setCheckedVendors}
          setBulkEditVendors={setBulkEditVendors}
        />
      )}
      {selectedVendor && (
        <VendorDetailsSlider
          selectedVendor={selectedVendor}
          setSelectedVendor={setSelectedVendor}
          setSelectedTransaction={setSelectedTransaction}
          linkedProducts={dbProducts
            .filter((product) =>
              isProductLinkedToVendor(
                product,
                selectedVendor,
                productLifecycleEvents
              )
            )
            .map((product) => {
              const lifecycle =
                productLifecycleEvents.find(
                  ({ productId }) => productId === product.id
                ) || {};
              return calculateMergedProductFootprint(
                product,
                lifecycle,
                emissionsFactors
              );
            })}
        />
      )}
      {selectedProduct && (
        <PlatformProductDetailsSlider
          selectedProduct={selectedProduct}
          setSelectedProduct={(p) => setSelectedProductId(p?.id ?? null)}
          setReportProduct={setReportProduct}
          otherProducts={dbProducts.filter(
            ({ id }) => id !== selectedProductId
          )}
        />
      )}
      {selectedTransaction && (
        <EditRowTransactions
          transaction={selectedTransaction || {}}
          setIsSlideOpen={setSelectedTransaction}
          saveButtonText="Save Changes"
        />
      )}
      {reportProduct && (
        <ProductReport
          setShowReport={setReportProduct}
          companyName={companyName}
          product={reportProduct}
        />
      )}
      {vendors.length ? (
        <DashboardViewLayout
          type="vendors"
          viewLoading={emissionsLoading || viewLoading}
          title="Vendors"
          subtitle={
            vendors.length
              ? `You have ${
                  vendors.length
                } vendors that have contributed ${formatDecimal(
                  convertCarbonUnits(vendorsTonsCo2e)
                )} ${carbonUnitLabel} to your balance`
              : "`You haven't loaded any vendors yet"
          }
          chips={filterChips}
          isFiltered={
            filterScopeThreeCategory ||
            filterSubcategories.length ||
            filterVendorSurveyStatus
          }
          filterPopperProps={{
            filterRows: buildFilterRows(),
            resetFilters: () => {
              setFilterSubcategories([]);
              setFilterScopeThreeCategory("");
              setFilterVendorSurveyStatus("");
            },
            title: "Filter Vendors"
          }}
          preGraph={<CleanupCalloutBanner rootPath={rootPath} />}
          primaryAction={
            <NewVendorsButtonMenu
              setShowNewVendorInput={setShowNewVendorInput}
            />
          }
          premiumActions={[
            <VendorCsvDownloadButton
              key="csv-download"
              vendors={filteredVendors}
            />,
            <NullableInput
              key="search-vendors"
              value={vendorSearchString}
              setValue={setVendorSearchString}
              label="Search Vendors"
            />
          ]}
          graph={
            !!chartVendors.length && (
              <VendorsGraphs
                vendors={[...chartVendors].sort(
                  (a, b) => a.tonsCo2e - b.tonsCo2e
                )}
                setSelectedVendor={selectVendorFromGraph}
              />
            )
          }
          tableAction={
            !!checkedVendors.length && (
              <CheckedVendorsActionBlock
                setBulkEditVendors={setBulkEditVendors}
                checkedVendors={checkedVendors}
                setCheckedVendors={setCheckedVendors}
              />
            )
          }
          table={
            <SortableTable
              rows={[...filteredVendors].sort(
                (a, b) => b.severity - a.severity
              )}
              rowComponent={(vendor) => (
                <VendorsTableRow
                  checkedVendors={checkedVendors}
                  setCheckedVendors={setCheckedVendors}
                  vendor={vendor}
                  setSelectedVendor={setSelectedVendor}
                />
              )}
              columns={tableColumns}
              currentPage={tablePage}
              setCurrentPage={setTablePage}
              rowsPerPage={VENDORS_TABLE_ROWS_PER_PAGE}
              onCheckAll={onCheckAllVendors}
              checkedRows={checkedVendors}
              setCheckedRows={setCheckedVendors}
            />
          }
          footer={<ActionItemsModule title="Vendors" type="vendors" />}
        />
      ) : (
        <MissingVendors setShowNewVendorInput={setShowNewVendorInput} />
      )}
    </>
  );
};

const VendorsView = (props) => (
  <ProductsEmissionsFactorsContextProvider>
    <FeatureErrorBoundary featureName="Vendor Management">
      <VendorsViewBlock {...props} />
    </FeatureErrorBoundary>
  </ProductsEmissionsFactorsContextProvider>
);
export default VendorsView;
